import { useState, useEffect, useCallback } from "react";
import { connectProvider, getConnections, disconnectProvider, pullConnectorReport, sageLogin, sageSave } from "./api";

/*
  ConnectedAccounts.jsx

  Two modes:

  1. Per-client (used inside ClientProfile.jsx):
       <ConnectedAccounts clientId={clientId} />
     Connections are tagged to that specific client; pulled reports save
     into that client's Reports list.

  2. Firm-wide (used on the dedicated Connect Account page, one per
     provider, reached from the sidebar / Overview cards):
       <ConnectedAccounts clientId={null} providerFilter="xero" showHeader />
     clientId={null} means "the tax agent's own account, not tied to any
     one client" — pulled reports are NOT auto-saved anywhere (there's no
     client to attach them to), they just display inline.
     providerFilter scopes the whole widget to one provider — hides the
     other three "Connect X" buttons and only shows that provider's
     connection(s).

  Xero / QuickBooks / Zoho all connect the same way: click Connect, full
  page redirect to the provider's consent screen, redirect back. Sage is
  different — there's no OAuth app for the South African API at all (see
  sage_connector.py), so clicking "Connect Sage" opens an inline form
  instead: the client's own Sage username, password, and their
  Sage-issued API key, then a company picker once those validate. See
  SageConnectForm below.

  Reuses the existing global .panel / .btn / .badge classes injected by
  ClientDashboard.jsx's <style>{styles}</style> — no new stylesheet needed.
*/

const PROVIDERS = [
  { key: "xero", label: "Xero", color: "#13B5EA", letter: "X" },
  { key: "quickbooks", label: "QuickBooks Online", color: "#2CA01C", letter: "Q" },
  { key: "sage", label: "Sage Business Cloud", color: "#00A88E", letter: "S" },
  { key: "zoho", label: "Zoho Books", color: "#E42527", letter: "Z" },
];

const REPORT_TYPES = [
  { key: "profit_and_loss", label: "Profit & Loss" },
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "trial_balance", label: "Trial Balance" },
];

function ProviderDot({ color, letter }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: letter ? 18 : 8,
        height: letter ? 18 : 8,
        borderRadius: letter ? 5 : "50%",
        background: color,
        marginRight: 8,
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 800,
        color: "#fff",
      }}
    >
      {letter || ""}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = status === "active" ? "badge-high" : status === "expired" ? "badge-medium" : "badge-low";
  return (
    <span className={`badge ${cls}`}>
      <span className="badge-dot" />
      {status}
    </span>
  );
}

function SageConnectForm({ clientId, onConnected, onCancel }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [companies, setCompanies] = useState(null); // null = not validated yet
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const inputStyle = {
    width: "100%",
    background: "rgba(59,158,255,0.04)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    color: "var(--text)",
    fontFamily: "'Outfit',sans-serif",
    fontSize: 12.5,
    padding: "8px 10px",
    marginBottom: 10,
  };

  async function handleValidate() {
    setBusy(true);
    setError("");
    try {
      const { companies: found } = await sageLogin(username, password, apiKey);
      setCompanies(found);
      if (found.length === 1) setSelectedCompanyId(found[0].id);
    } catch (e) {
      setError(e.message || "Could not validate those Sage credentials");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    const company = companies.find((c) => c.id === selectedCompanyId);
    if (!company) {
      setError("Pick a company first.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await sageSave(username, password, apiKey, company.id, company.name, clientId);
      onConnected();
    } catch (e) {
      setError(e.message || "Could not save this Sage connection");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="file-item" style={{ flexDirection: "column", alignItems: "stretch", gap: 4, marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <ProviderDot color="#00A88E" letter="S" />
        <div style={{ fontWeight: 600 }}>Connect Sage Business Cloud Accounting</div>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 10 }}>
        Sage's South African API doesn't support the usual "sign in and approve" screen — enter your client's own
        Sage login and their Sage-issued API key instead (Settings → API inside their Sage account).
      </div>

      {error && (
        <div className="vat-error-banner" style={{ padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
          {error}
        </div>
      )}

      {companies === null ? (
        <>
          <input style={inputStyle} placeholder="Sage username (email)" value={username} onChange={(e) => setUsername(e.target.value)} />
          <input style={inputStyle} type="password" placeholder="Sage password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <input style={inputStyle} placeholder="Sage API key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-blue btn-sm" disabled={busy || !username || !password || !apiKey} onClick={handleValidate}>
              {busy ? "Checking…" : "Validate & continue"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, marginBottom: 8 }}>Which company?</div>
          <select
            style={inputStyle}
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Select a company…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-blue btn-sm" disabled={busy || !selectedCompanyId} onClick={handleSave}>
              {busy ? "Connecting…" : "Connect this company"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ConnectedAccounts({ clientId = null, providerFilter = null, showHeader = true }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [sageFormOpen, setSageFormOpen] = useState(false);
  const [pullingId, setPullingId] = useState(null);
  const [reportTypeByConn, setReportTypeByConn] = useState({});
  const [lastPulled, setLastPulled] = useState(null); // { connectionId, reportType, rows, pulledAt }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Always fetch unfiltered and filter client-side: the API only
      // applies its own client_id filter when one is given, which can't
      // tell "no filter" apart from "filter to firm-wide (client_id=null)"
      // — the exact distinction this component needs to get right.
      const data = await getConnections();
      const scoped = (data.connections || []).filter((c) =>
        clientId == null ? c.client_id == null : String(c.client_id) === String(clientId)
      );
      setConnections(providerFilter ? scoped.filter((c) => c.provider === providerFilter) : scoped);
    } catch (e) {
      setError(e.message || "Could not load connected accounts");
    } finally {
      setLoading(false);
    }
  }, [clientId, providerFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // If we just landed back from a provider's OAuth consent screen for THIS
  // scope (this client, or — if clientId is null — this firm-wide page),
  // re-fetch and clear the query string. In practice ClientDashboard.jsx's
  // own top-level effect usually wins this race and already cleaned the
  // URL before this ever mounts, but this stays as a defense-in-depth
  // fallback (e.g. a direct deep link while already mounted).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    if (!connected) return;
    const urlClientId = params.get("client_id");
    const isMatch = clientId == null ? !urlClientId : String(urlClientId) === String(clientId);
    if (isMatch) {
      refresh();
      params.delete("connected");
      params.delete("client_id");
      const cleanUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [clientId, refresh]);

  async function handleConnect(providerKey) {
    if (providerKey === "sage") {
      setSageFormOpen(true);
      return;
    }
    setConnectingProvider(providerKey);
    setError("");
    try {
      const { authorize_url } = await connectProvider(providerKey, clientId);
      window.location.href = authorize_url; // full navigation — provider needs to render its own consent page
    } catch (e) {
      setError(e.message || `Could not start ${providerKey} connection`);
      setConnectingProvider(null);
    }
  }

  async function handleDisconnect(connectionId) {
    try {
      await disconnectProvider(connectionId);
      setConnections((prev) => prev.filter((c) => c.id !== connectionId));
    } catch (e) {
      setError(e.message || "Could not disconnect");
    }
  }

  async function handlePull(connectionId) {
    const reportType = reportTypeByConn[connectionId] || "profit_and_loss";
    setPullingId(connectionId);
    setError("");
    try {
      const result = await pullConnectorReport(connectionId, reportType);
      setLastPulled({
        connectionId,
        reportType,
        rows: result.rows || [],
        pulledAt: result.pulled_at,
        savedToReports: Boolean(result.report_id),
      });
      refresh(); // updates last_synced_at
    } catch (e) {
      setError(e.message || "Report pull failed");
    } finally {
      setPullingId(null);
    }
  }

  const connectedKeys = new Set(connections.map((c) => c.provider));
  const filterMeta = providerFilter ? PROVIDERS.find((p) => p.key === providerFilter) : null;
  const availableProviders = providerFilter ? PROVIDERS.filter((p) => p.key === providerFilter) : PROVIDERS;

  return (
    <div className="panel" style={{ marginTop: showHeader ? 20 : 0 }}>
      {showHeader && (
        <div className="panel-header">
          <div className="panel-title">{filterMeta ? `Connect ${filterMeta.label}` : "Connect Account"}</div>
        </div>
      )}
      <div className="panel-body">
        {error && (
          <div className="vat-error-banner" style={{ padding: "10px 14px", marginBottom: 14 }}>
            {error}
          </div>
        )}

        {/* Connect buttons — one per provider not yet connected in this scope */}
        {!sageFormOpen && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
            {availableProviders.filter((p) => !connectedKeys.has(p.key)).map((p) => (
              <button
                key={p.key}
                className="btn btn-ghost btn-sm"
                disabled={connectingProvider === p.key}
                onClick={() => handleConnect(p.key)}
              >
                <ProviderDot color={p.color} letter={p.letter} />
                {connectingProvider === p.key ? "Redirecting…" : p.key === "sage" ? `Set up ${p.label}` : `Connect ${p.label}`}
              </button>
            ))}
          </div>
        )}

        {sageFormOpen && (
          <SageConnectForm
            clientId={clientId}
            onCancel={() => setSageFormOpen(false)}
            onConnected={() => {
              setSageFormOpen(false);
              refresh();
            }}
          />
        )}

        {loading ? (
          <div style={{ color: "var(--muted)", fontSize: 13 }}>Loading…</div>
        ) : connections.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: 13 }}>
            {filterMeta
              ? `${filterMeta.label} isn't connected yet.`
              : clientId == null
              ? "No accounting platforms connected yet."
              : "No accounting platforms connected for this client yet."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {connections.map((c) => {
              const provider = PROVIDERS.find((p) => p.key === c.provider);
              return (
                <div key={c.id} className="file-item" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ProviderDot color={provider?.color || "#888"} letter={provider?.letter} />
                      <div>
                        <div style={{ fontWeight: 600 }}>{provider?.label || c.provider}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>
                          {c.provider_tenant_name || c.provider_tenant_id}
                          {c.last_synced_at ? ` · last pulled ${new Date(c.last_synced_at).toLocaleString()}` : " · never pulled"}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <StatusBadge status={c.status} />
                      <button className="btn btn-danger btn-sm" onClick={() => handleDisconnect(c.id)}>
                        Disconnect
                      </button>
                    </div>
                  </div>

                  {c.status === "active" && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        value={reportTypeByConn[c.id] || "profit_and_loss"}
                        onChange={(e) => setReportTypeByConn((prev) => ({ ...prev, [c.id]: e.target.value }))}
                        style={{
                          background: "rgba(59,158,255,0.04)",
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          color: "var(--text)",
                          fontFamily: "'Outfit',sans-serif",
                          fontSize: 12.5,
                          padding: "7px 10px",
                        }}
                      >
                        {REPORT_TYPES.map((r) => (
                          <option key={r.key} value={r.key}>{r.label}</option>
                        ))}
                      </select>
                      <button
                        className="btn btn-blue btn-sm"
                        disabled={pullingId === c.id}
                        onClick={() => handlePull(c.id)}
                      >
                        {pullingId === c.id ? "Pulling…" : "Pull report"}
                      </button>
                    </div>
                  )}

                  {c.status !== "active" && (
                    <div style={{ fontSize: 11.5, color: "var(--error)" }}>
                      Connection {c.status} — reconnect above to keep pulling reports.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {lastPulled && lastPulled.rows.length > 0 && (
          <div className="net-bar" style={{ flexDirection: "column", alignItems: "stretch", marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <strong style={{ fontSize: 12.5 }}>
                {REPORT_TYPES.find((r) => r.key === lastPulled.reportType)?.label} — {lastPulled.rows.length} lines
              </strong>
              {lastPulled.savedToReports && (
                <span className="badge badge-high">
                  <span className="badge-dot" /> saved to Reports
                </span>
              )}
            </div>
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {lastPulled.rows.slice(0, 50).map((row, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    padding: "4px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <span style={{ color: "var(--muted-light)" }}>
                    {row.section ? `${row.section} · ` : ""}{row.account_name}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace" }}>
                    {row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              {lastPulled.rows.length > 50 && (
                <div style={{ fontSize: 11, color: "var(--muted)", padding: "6px 0" }}>
                  + {lastPulled.rows.length - 50} more lines
                  {lastPulled.savedToReports ? " — open it from this client's Reports list." : "."}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
